import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, bg = false, children }) => (
  <section className={`py-12 md:py-16 ${bg ? 'bg-gray-50 dark:bg-white/[0.02]' : ''}`}>
    <div className="max-w-4xl mx-auto px-6">
      {title && <h2 className="text-2xl font-display text-gray-900 dark:text-white mb-6 underline decoration-brand-500/30 decoration-4 underline-offset-8">{title}</h2>}
      <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const Terms = () => {
  const updatedDate = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0f0d]">
      {/* Hero Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 px-6 border-b border-gray-100 dark:border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-[0.2em] uppercase text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800/50 rounded-full bg-brand-50 dark:bg-brand-900/20"
          >
            Agreement
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-4"
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-gray-500 dark:text-gray-500 text-sm"
          >
            Effective Date: {updatedDate}
          </motion.p>
        </div>
      </section>

      <Section title="1. Acceptance of Terms">
        <p>
          By accessing and using Budbreak Innovations' services, including our website, mobile applications, and autonomous scouting equipment, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our services.
        </p>
      </Section>

      <Section title="2. Description of Service" bg>
        <p>
          Budbreak Innovations provides AI-powered vineyard management solutions and hardware, including but not limited to:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>Autonomous vineyard scanning and robotics</li>
          <li>Real-time vine health and disease analysis</li>
          <li>Precision mapping and spatial data visualization</li>
          <li>Integration with the BudBase analytics platform</li>
        </ul>
      </Section>

      <Section title="3. User Responsibilities">
        <p>
          Users are responsible for ensuring the safety and accessibility of their vineyards during autonomous scanning operations. This includes:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>Providing accurate block maps and boundary information</li>
          <li>Maintaining clear rows free of significant obstructions</li>
          <li>Ensuring authorized personnel are aware of robot deployment schedules</li>
          <li>Protecting project-specific data credentials</li>
        </ul>
      </Section>

      <Section title="4. Intellectual Property" bg>
        <p>
          All proprietary technology, including computer vision models, robotic hardware designs, and data processing algorithms, are the exclusive property of Budbreak Innovations. Customers retain ownership of their raw vineyard data but grant Budbreak a license to use de-identified data for model improvement and research purposes.
        </p>
      </Section>

      <Section title="5. Limitation of Liability">
        <p>
          Budbreak Innovations provides agricultural insights based on AI analysis. While we strive for extreme accuracy, our reports are for informational purposes only. Farmers should verify critical vineyard decisions with on-ground experts. We are not liable for crop loss or damages resulting from decisions made based on our data.
        </p>
      </Section>

      <Section title="6. Contact & Support" bg>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="p-6 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-[#141a17]">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Email Support</h4>
            <a href="mailto:info@budbreakinnovations.com" className="text-brand-600 dark:text-brand-400 hover:underline">
              info@budbreakinnovations.com
            </a>
          </div>
          <div className="p-6 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-[#141a17]">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Technical Assistance</h4>
            <p className="text-brand-600 dark:text-brand-400">(707) 266-6138</p>
          </div>
        </div>
      </Section>

      {/* Footer spacer */}
      <div className="py-12"></div>
    </div>
  );
};

export default Terms;